import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  name: "",
  phone: "",
  loan_amount: "",
  loan_date: todayISO(),
  interest_rate: "0",
  collection_method: "a_vista",
  collection_frequency: "mensal",
  installments_count: "1",
  installment_amount: "",
  first_payment_date: todayISO(),
  notes: "",
  custom_daily_late_fee: "",
  contract_type: "emprestimo",
};

const DRAFT_KEY = "msf:draft:client:new";

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || typeof d !== "object") return null;
    return { ...empty, ...d };
  } catch {
    return null;
  }
}

function saveDraft(form) {
  try {
    const hasContent =
      form.name || form.phone || form.loan_amount || form.notes ||
      form.custom_daily_late_fee || form.installment_amount;
    if (!hasContent) {
      localStorage.removeItem(DRAFT_KEY);
    } else {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    }
  } catch {}
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

export default function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (open) {
      if (client) {
        setForm({
          name: client.name || "",
          phone: client.phone || "",
          loan_amount: String(client.loan_amount ?? ""),
          loan_date: client.loan_date || todayISO(),
          interest_rate: String(client.interest_rate ?? "0"),
          collection_method: client.collection_method || "a_vista",
          collection_frequency: client.collection_frequency || "mensal",
          installments_count: String(client.installments_count ?? "1"),
          installment_amount: client.installment_amount ? String(client.installment_amount) : "",
          first_payment_date: client.first_payment_date || todayISO(),
          notes: client.notes || "",
          custom_daily_late_fee:
            client.custom_daily_late_fee != null ? String(client.custom_daily_late_fee) : "",
          contract_type: client.contract_type || "emprestimo",
        });
        setRestored(false);
      } else {
        const draft = loadDraft();
        if (draft) {
          setForm(draft);
          setRestored(true);
        } else {
          setForm(empty);
          setRestored(false);
        }
      }
    }
  }, [open, client]);

  // Persiste rascunho apenas ao criar novo cliente
  useEffect(() => {
    if (open && !client) saveDraft(form);
  }, [form, open, client]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const discardDraft = () => {
    clearDraft();
    setForm(empty);
    setRestored(false);
    toast.info("Rascunho descartado");
  };

  const submit = async (e, generateContract = false) => {
    e.preventDefault();
    if (generateContract && !e.currentTarget.closest("form")?.reportValidity()) return;
    // Abre a aba durante o gesto do usuário para evitar bloqueio de pop-up.
    const contractWindow = generateContract ? window.open("about:blank", "_blank") : null;
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        loan_amount: parseFloat(form.loan_amount) || 0,
        loan_date: form.loan_date,
        interest_rate: parseFloat(form.interest_rate) || 0,
        collection_method: form.collection_method,
        collection_frequency: form.collection_frequency,
        installments_count:
          form.collection_method === "a_vista" ? 1 : parseInt(form.installments_count) || 1,
        installment_amount: form.installment_amount ? parseFloat(form.installment_amount) : null,
        first_payment_date: form.first_payment_date,
        notes: form.notes,
        custom_daily_late_fee: form.custom_daily_late_fee
          ? parseFloat(form.custom_daily_late_fee)
          : null,
        contract_type: form.contract_type || "emprestimo",
      };
      if (client) {
        await api.patch(`/clients/${client.id}`, payload);
        toast.success("Cliente atualizado com sucesso");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente adicionado com sucesso");
        clearDraft();
      }
      if (generateContract) {
        const amount = Number(payload.loan_amount || 0);
        const rate = Number(payload.interest_rate || 0);
        const params = new URLSearchParams({
          name: payload.name,
          phone: payload.phone,
          business_name: "MS Soluções Financeiras",
          valor: String(amount),
          taxa: String(rate),
          quitar: String(amount * (1 + rate / 100)),
          first_date: payload.first_payment_date || payload.loan_date || "",
          guarantee_type: "sem_garantia",
          contract_type: payload.contract_type,
        });
        if (contractWindow) {
          contractWindow.location.href = `https://mssystem.onrender.com/billing/contract.pdf?${params.toString()}`;
        } else {
          window.open(`https://mssystem.onrender.com/billing/contract.pdf?${params.toString()}`, "_blank", "noopener,noreferrer");
        }
      }
      onSaved?.();
    } catch (err) {
      if (contractWindow && !contractWindow.closed) contractWindow.close();
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B132B] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="client-form-dialog"
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            {client ? "Editar Cliente" : "Novo Cliente"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Preencha os dados do empréstimo e a forma de cobrança.
          </DialogDescription>
        </DialogHeader>

        {!client && restored && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            data-testid="cf-draft-banner"
          >
            <span>Rascunho anterior restaurado — você pode continuar de onde parou.</span>
            <button
              type="button"
              onClick={discardDraft}
              className="text-amber-300 underline underline-offset-2 hover:text-amber-100"
              data-testid="cf-draft-discard"
            >
              Descartar
            </button>
          </div>
        )}

        <form onSubmit={(e) => submit(e, false)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Nome *</Label>
              <Input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Nome completo"
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="cf-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Telefone</Label>
              <Input
                value={form.phone}
                onChange={set("phone")}
                placeholder="(11) 99999-9999"
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="cf-phone"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Valor emprestado (R$) *</Label>
              <Input
                required
                type="number"
                step="0.01"
                min="0"
                value={form.loan_amount}
                onChange={set("loan_amount")}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                data-testid="cf-loan-amount"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Data do empréstimo *</Label>
              <Input
                required
                type="date"
                value={form.loan_date}
                onChange={set("loan_date")}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="cf-loan-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Taxa de juros (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.interest_rate}
                onChange={set("interest_rate")}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                data-testid="cf-interest"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Forma de cobrança *</Label>
              <Select value={form.collection_method} onValueChange={set("collection_method")}>
                <SelectTrigger
                  className="bg-slate-900/60 border-slate-700"
                  data-testid="cf-method"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="a_vista">À Vista (Pagamento único)</SelectItem>
                  <SelectItem value="parcelado">Parcelado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.collection_method === "parcelado" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-slate-300">Frequência</Label>
                  <Select
                    value={form.collection_frequency}
                    onValueChange={set("collection_frequency")}
                  >
                    <SelectTrigger className="bg-slate-900/60 border-slate-700" data-testid="cf-freq">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                      <SelectItem value="diario">Diário</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="quinzenal">Quinzenal</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300">Nº de parcelas</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.installments_count}
                    onChange={set("installments_count")}
                    className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                    data-testid="cf-installments"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-slate-300">Valor por parcela (opcional)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Deixe vazio para dividir automaticamente"
                    value={form.installment_amount}
                    onChange={set("installment_amount")}
                    className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                    data-testid="cf-inst-amount"
                  />
                </div>
              </>
            )}

            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-slate-300">Tipo de contrato *</Label>
              <Select value={form.contract_type || "emprestimo"} onValueChange={set("contract_type")}>
                <SelectTrigger
                  className="bg-slate-900/60 border-slate-700"
                  data-testid="cf-contract-type"
                >
                  <SelectValue placeholder="Selecione o tipo de contrato" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="emprestimo">Contrato de empréstimo (padrão da simulação)</SelectItem>
                  <SelectItem value="confissao_divida">Contrato de confissão de dívida</SelectItem>
                  <SelectItem value="reconhecimento_divida">Termo de reconhecimento de dívida</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-slate-300">Data do primeiro pagamento *</Label>
              <Input
                required
                type="date"
                value={form.first_payment_date}
                onChange={set("first_payment_date")}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="cf-first-date"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-slate-300">
                Juros diário de atraso (R$/dia) — opcional
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Deixe vazio para calcular automaticamente"
                value={form.custom_daily_late_fee}
                onChange={set("custom_daily_late_fee")}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                data-testid="cf-custom-late-fee"
              />
              <div className="text-[11px] text-slate-500">
                Ex.: R$ 5,00/dia fixo. Se vazio, o sistema cobra <code>arredondar((principal+juros)/30)</code> por dia após o vencimento.
              </div>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-slate-300">Observações</Label>
              <Textarea
                value={form.notes}
                onChange={set("notes")}
                rows={2}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="cf-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => onOpenChange(false)}
              data-testid="cf-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="cf-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : client ? "Salvar" : "Adicionar sem contrato"}
            </Button>
            {!client && (
              <Button
                type="button"
                disabled={busy}
                onClick={(e) => submit(e, true)}
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
                data-testid="cf-submit-generate-contract"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Adicionar e gerar contrato PDF"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
